/**
 * Environment utilities for dynamic host detection in development
 * Note: Logging in common package uses console since this is used in Edge Runtime
 */

/**
 * Get the current host for development environment
 * Uses actual IP address instead of localhost for cross-device testing
 */
export function getDevHost(): string {
  if (typeof window === 'undefined') {
    // Server-side: return localhost
    return 'localhost';
  }

  // Client-side: use actual hostname from browser
  const hostname = window.location.hostname;

  // If already using an IP address or non-localhost domain, use it
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    console.log('[Host Config] Using IP/domain host:', hostname);
    return hostname;
  }

  // For localhost, we'll use the hostname as-is
  // The actual IP will be resolved by the browser when accessing from other devices
  return hostname;
}

/**
 * Detects if we're in a Vite environment
 * Exported for use in other packages
 *
 * Note: This check happens in a pre-bundled package, so Vite's define replacements
 * won't work here. This function checks for runtime globals only.
 * For reliable Vite detection, check directly in the app bundle code.
 */
export function isViteEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  // Check globalThis (for manual assignment or runtime globals)
  if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
    return true;
  }

  // Check for __vite__ runtime global (development mode)
  if (typeof (globalThis as any).__vite__ !== 'undefined') {
    return true;
  }

  return false;
}

export type AppEnv = 'production' | 'staging' | 'development';

function normalizeAppEnv(value?: string): AppEnv {
  if (!value) {
    return 'development';
  }

  const normalized = value.toLowerCase();
  if (normalized === 'production' || normalized === 'prod') {
    return 'production';
  }
  if (normalized === 'staging' || normalized === 'stage' || normalized === 'preview') {
    return 'staging';
  }
  if (
    normalized === 'development' ||
    normalized === 'dev' ||
    normalized === 'local' ||
    normalized === 'test'
  ) {
    return 'development';
  }

  return 'development';
}

/**
 * Vercel environment mapping.
 *
 * Vercel has three environment types:
 *   production → main branch (production)
 *   preview    → staging branch (preview)
 *   custom     → development custom environment (requires actual env_* ID)
 *
 * The custom environment ID is project-specific and set via VERCEL_CUSTOM_ENV_ID.
 * Get it from: Vercel Dashboard → Project → Settings → Environments → dev → URL contains the ID
 * Or via API: GET /v9/projects/{projectId}/custom-environments → environments[].id
 */
export type VercelEnv = 'production' | 'preview' | 'custom';

export interface VercelDomainTarget {
  /** Git branch for preview deployments (staging) */
  gitBranch?: string;
  /** Vercel custom environment ID for development (env_* format) */
  customEnvironmentId?: string;
}

/**
 * Map OrderUp app environment to Vercel Add Domain API targeting params.
 *
 *   production  → {} (production deployment, main branch)
 *   staging     → { gitBranch: 'staging' } (preview deployment)
 *   development → resolved at provisioning time by fetching the custom env ID from Vercel API
 *
 * For development, pass customEnvironmentId obtained from the Vercel API
 * via unifiedDomainService.resolveVercelCustomEnvId().
 */
export function getVercelDomainTarget(appEnv: AppEnv | string, customEnvironmentId?: string): VercelDomainTarget {
  const normalized = normalizeAppEnv(typeof appEnv === 'string' ? appEnv : 'development');
  switch (normalized) {
    case 'production':
      return {};
    case 'staging':
      return { gitBranch: 'staging' };
    case 'development':
    default:
      if (customEnvironmentId) {
        return { customEnvironmentId };
      }
      return { gitBranch: 'development' };
  }
}

/**
 * Map OrderUp app environment to Vercel environment label for display/storage.
 */
export function appEnvToVercelEnv(appEnv: AppEnv | string): VercelEnv {
  const normalized = normalizeAppEnv(typeof appEnv === 'string' ? appEnv : 'development');
  switch (normalized) {
    case 'production':
      return 'production';
    case 'staging':
      return 'preview';
    case 'development':
    default:
      return 'custom';
  }
}

/**
 * Gets APP_ENV in a framework-agnostic way
 * Defaults to 'development' for safety if detection fails
 * Exported for use in other packages
 */
export function getAppEnv(): AppEnv {
  let detected: string | undefined;

  // Browser environment
  if (typeof window !== 'undefined') {
    const isVite = isViteEnvironment();

    if (isVite) {
      // Vite environment - try __VITE_ENV__ global
      try {
        if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
          const envBag = (globalThis as any).__VITE_ENV__;
          detected = envBag.APP_ENV || envBag.VITE_APP_ENV || envBag.MODE;
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[Environment] Failed to access Vite APP_ENV:', error);
        }
      }
    } else {
      // Next.js browser environment - use direct reference for inlining
      detected = process.env.NEXT_PUBLIC_APP_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV;
    }
  } else {
    // Server-side environment
    if (typeof process !== 'undefined' && process.env) {
      detected =
        process.env.APP_ENV ||
        process.env.NEXT_PUBLIC_APP_ENV ||
        process.env.VERCEL_ENV ||
        process.env.NODE_ENV;
    }
  }

  if (!detected && process.env.NODE_ENV === 'development') {
    console.warn('[Environment] APP_ENV not found, defaulting to development for safety');
  }

  // Default to development for safety - better to use dev resources than accidentally hit prod
  return normalizeAppEnv(detected);
}

/**
 * Gets any environment variable in a framework-agnostic way
 * Exported for use in other packages
 * @param key - The environment variable key (without NEXT_PUBLIC_ or VITE_ prefix)
 * @param fallback - Fallback value if not found
 */
export function getFrameworkAgnosticEnvVar(key: string, fallback: string): string {
  if (typeof window !== 'undefined') {
    const isVite = isViteEnvironment();
    if (isVite) {
      try {
        if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
          return (globalThis as any).__VITE_ENV__[key] || fallback;
        }
      } catch {
        // Intentionally ignore errors accessing Vite env
      }
    } else {
      // Next.js browser environment - use direct reference for inlining
      const value = (process.env as any)[`NEXT_PUBLIC_${key}`];
      return value || fallback;
    }
  } else if (typeof process !== 'undefined' && process.env) {
    return (process.env as any)[`NEXT_PUBLIC_${key}`] || (process.env as any)[key] || fallback;
  }
  return fallback;
}

/**
 * Gets explicit AUTH_SERVICE_URL in a framework-agnostic way
 * Exported for use in other packages
 */
export function getExplicitAuthServiceUrl(): string | undefined {
  // Browser environment
  if (typeof window !== 'undefined') {
    const isVite = isViteEnvironment();

    if (isVite) {
      // Vite environment
      try {
        if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
          return (globalThis as any).__VITE_ENV__.AUTH_SERVICE_URL;
        }
      } catch (error) {
        console.warn('[Environment] Failed to access Vite AUTH_SERVICE_URL:', error);
      }
    } else {
      // Next.js browser environment - use direct reference for inlining
      const AUTH_SERVICE_URL = process.env.NEXT_PUBLIC_AUTH_SERVICE_URL;
      if (AUTH_SERVICE_URL) {
        return AUTH_SERVICE_URL;
      }
    }
  } else {
    // Server-side environment
    if (typeof process !== 'undefined' && process.env) {
      return process.env.NEXT_PUBLIC_AUTH_SERVICE_URL || process.env.AUTH_SERVICE_URL;
    }
  }

  return undefined;
}

/**
 * Get environment-aware URLs for services
 * Uses NODE_ENV to distinguish local dev (development) from builds (production)
 */
export function getServiceUrls() {
  // NODE_ENV = 'production' for ANY build (Vercel dev/preview/prod)
  // NODE_ENV = 'development' ONLY for local dev server (npm/pnpm dev)
  let isLocalDev = false;

  if (typeof window !== 'undefined') {
    // Browser environment - check for Vite or Next.js
    const isVite = isViteEnvironment();
    if (isVite) {
      // Vite: check MODE from __VITE_ENV__
      try {
        if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
          isLocalDev = (globalThis as any).__VITE_ENV__.MODE === 'development';
        }
      } catch {
        // Fallback: if we can't determine, assume not local dev
        isLocalDev = false;
      }
    } else {
      // Next.js browser: check NODE_ENV with direct reference for inlining
      isLocalDev = process.env.NODE_ENV === 'development';
    }
  } else {
    // Server environment
    isLocalDev = process.env.NODE_ENV === 'development';
  }

  if (!isLocalDev) {
    // Any build (cloud deployment): use environment variables or cloud defaults
    // APP_ENV determines which cloud environment (dev/staging/prod)
    const appEnv = getAppEnv();

    console.log('[Environment] getServiceUrls - appEnv:', appEnv);

    // Cloud defaults based on APP_ENV
    console.log('[Environment] Using cloud defaults for appEnv:', appEnv);
    let defaults: Record<string, string>;
    if (appEnv === 'production') {
      defaults = {
        auth: 'https://identity.letsorderup.com',
        admin: 'https://www.letsorderup.site',
        // OrderUp staff suite (apps/crm). `.site` is the STAFF domain — admin and
        // billing already live there; `.com` is customer-facing.
        crm: 'https://crm.letsorderup.site',
        api: 'https://api.letsorderup.com',
        menu: 'https://menu.letsorderup.app',
        management: 'https://app.letsorderup.com',
        staff: 'https://my.letsorderup.com',
        restaurant: 'https://www.letsorderup.online',
        ordering: 'https://now.letsorderup.online',
        checkout: 'https://checkout.letsorderup.online',
        onboarding: 'https://onboarding.letsorderup.com',
        billing: 'https://billing.letsorderup.site',
      };
    } else if (appEnv === 'staging') {
      defaults = {
        auth: 'https://staging.identity.letsorderup.com',
        admin: 'https://staging.letsorderup.site',
        crm: 'https://staging.crm.letsorderup.site',
        api: 'https://staging.api.letsorderup.com',
        menu: 'https://staging.menu.letsorderup.app',
        management: 'https://staging.app.letsorderup.com',
        staff: 'https://staging.my.letsorderup.com',
        restaurant: 'https://staging.letsorderup.online',
        ordering: 'https://staging.now.letsorderup.online',
        checkout: 'https://staging.checkout.letsorderup.online',
        onboarding: 'https://staging.onboarding.letsorderup.com',
        billing: 'https://staging.billing.letsorderup.site',
      };
    } else {
      // development (letsorderup.dev)
      defaults = {
        auth: 'https://auth.letsorderup.dev',
        admin: 'https://admin.letsorderup.dev',
        crm: 'https://crm.letsorderup.dev',
        api: 'https://api.letsorderup.dev',
        menu: 'https://menu.letsorderup.dev',
        management: 'https://management.letsorderup.dev',
        staff: 'https://staff.letsorderup.dev',
        restaurant: 'https://restaurant.letsorderup.dev',
        ordering: 'https://ordering.letsorderup.dev',
        checkout: 'https://checkout.letsorderup.dev',
        onboarding: 'https://onboarding.letsorderup.dev',
        billing: 'https://billing.letsorderup.dev',
      };
    }

    // Allow explicit env vars to override individual URLs
    const getEnvVar = getFrameworkAgnosticEnvVar;
    const explicitAuthUrl = getExplicitAuthServiceUrl();

    return {
      ...defaults,
      ...(explicitAuthUrl && { auth: explicitAuthUrl }),
      admin: getEnvVar('ADMIN_URL', defaults.admin),
      crm: getEnvVar('CRM_URL', defaults.crm),
      api: getEnvVar('API_URL', defaults.api),
      menu: getEnvVar('MENU_URL', defaults.menu),
      management: getEnvVar('MANAGEMENT_URL', defaults.management),
      staff: getEnvVar('STAFF_URL', defaults.staff),
      restaurant: getEnvVar('RESTAURANT_URL', defaults.restaurant),
      ordering: getEnvVar('ORDERING_URL', defaults.ordering),
      onboarding: getEnvVar('ONBOARDING_URL', defaults.onboarding),
      billing: getEnvVar('BILLING_URL', defaults.billing),
    };
  }

  // Local development (NODE_ENV=development): use localhost with ports
  const host = getDevHost();

  return {
    auth: `http://${host}:3000`,
    admin: `http://${host}:3001`,
    crm: `http://${host}:3013`, // OrderUp staff suite (apps/crm)
    api: `http://${host}:3002`,
    menu: `http://${host}:3003`, // Vite menu app
    management: `http://${host}:3005`, // Management app (check package.json for actual port)
    staff: `http://${host}:3011`, // Staff self-service app (Next.js)
    restaurant: `http://${host}:3004`, // Restaurant app if exists
    ordering: `http://${host}:3006`, // Standalone ordering app (Next.js)
    billing: `http://${host}:3008`, // Billing app (Next.js)
    onboarding: `http://${host}:3009`, // Onboarding app (Vite/React)
  };
}

/**
 * Gets the onboarding service URL
 * @returns The onboarding service URL
 */
export function getOnboardingServiceUrl(): string {
  const urls = getServiceUrls();
  return urls.onboarding;
}

/**
 * Gets the management service URL.
 * Used by the auth app's `<ManagementRedirect />` to bounce users to the
 * management app once their client-onboarding provisioning completes.
 * @returns The management service URL
 */
export function getManagementServiceUrl(): string {
  const urls = getServiceUrls();
  return urls.management;
}

/**
 * Get redirect URLs for authentication flows
 */
export function getRedirectUrls() {
  const urls = getServiceUrls();

  return {
    default: urls.auth,
    admin: urls.admin,
    api: urls.api,
    management: urls.management,
  };
}

/**
 * Get CORS allowed origins dynamically
 */
export function getAllowedOrigins(): string[] {
  const urls = getServiceUrls();

  // Always include the main service URLs
  const origins = [
    urls.auth,
    urls.admin,
    urls.api,
  ];

  // Add menu app if it exists
  if (urls.menu) {
    origins.push(urls.menu);
  }

  // Add management app if it exists
  if (urls.management) {
    origins.push(urls.management);
  }

  // Add staff self-service app if it exists
  if (urls.staff) {
    origins.push(urls.staff);
  }

  // Add restaurant app if it exists
  if (urls.restaurant) {
    origins.push(urls.restaurant);
  }

  // In development, also allow localhost variations for fallback
  const isDev =
    process.env.NODE_ENV === 'development' ||
    getAppEnv() === 'development';

  if (isDev) {
    origins.push(
      'http://localhost:3000',
      'http://localhost:3001',
      'http://localhost:3002',
      'http://localhost:3003',
      'http://localhost:3004',
      'http://localhost:3005',
      'http://localhost:3011',
      'http://127.0.0.1:3000',
      'http://127.0.0.1:3001',
      'http://127.0.0.1:3002',
      'http://127.0.0.1:3003',
      'http://127.0.0.1:3004',
      'http://127.0.0.1:3005',
      'http://127.0.0.1:3011'
    );
  }

  return [...new Set(origins)].filter((o): o is string => o !== undefined); // Remove duplicates and undefined
}

/**
 * The set of OrderUp service origins that are legitimate redirect / auth-handoff
 * targets. Broader than {@link getAllowedOrigins} (which is the CORS set): this
 * intentionally includes every first-party app — ordering, checkout, onboarding,
 * billing, menu — because any of them can initiate a login and receive the
 * post-auth token handoff.
 *
 * SECURITY: this backs {@link isAllowedRedirectUrl}, the defense against
 * open-redirect token exfiltration. A `returnUrl` whose origin is not in this
 * set must NEVER receive a Firebase ID token.
 */
export function getAllowedRedirectOrigins(): string[] {
  const urls = getServiceUrls() as Record<string, string | undefined>;
  const candidates: string[] = Object.values(urls).filter(
    (u): u is string => typeof u === 'string' && u.length > 0
  );

  const isDev =
    process.env.NODE_ENV === 'development' || getAppEnv() === 'development';
  if (isDev) {
    // Local dev servers span ports 3000-3013 (see CLAUDE.md app table; staff =
    // 3011, loyalty = 3012, crm = 3013). This CEILING IS LOAD-BEARING: a port
    // above it is silently refused by `isAllowedRedirectUrl`, the auth app logs
    // "Rejected returnUrl outside the OrderUp origin allowlist" and falls back
    // to the admin app, so a new app appears to sign in and lands somewhere
    // else. Raise it in the same commit that claims a new port.
    for (let port = 3000; port <= 3013; port++) {
      candidates.push(`http://localhost:${port}`, `http://127.0.0.1:${port}`);
    }
  }

  const origins = candidates
    .map((u) => {
      try {
        return new URL(u).origin;
      } catch {
        return undefined;
      }
    })
    .filter((o): o is string => o !== undefined);

  return [...new Set(origins)];
}

/**
 * Validates a caller-supplied `returnUrl` / redirect target against the OrderUp
 * origin allowlist. Returns true ONLY for absolute http(s) URLs whose origin is
 * a known first-party OrderUp service origin.
 *
 * Relative URLs, non-http(s) schemes (e.g. `javascript:`), and unparseable
 * values are rejected — callers must fall back to a safe default rather than
 * honoring the supplied value. This is the guard against open-redirect token
 * exfiltration in the auth app's cross-app handoff.
 */
export function isAllowedRedirectUrl(candidate: string | null | undefined): boolean {
  if (!candidate) return false;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  return getAllowedRedirectOrigins().includes(parsed.origin);
}
